import React, { useState, useEffect } from 'react';
import { Phone, X, Edit2, Copy, Check } from 'lucide-react';
import { getScript, getAllScripts } from '../../../database/db';
import { getCustomField } from '../../services/perfex-api';

/**
 * Call Script Modal Component
 * Displays personalized call script for lead follow-ups
 *
 * @param {boolean} show - Whether to show the modal
 * @param {function} onClose - Callback when modal is closed
 * @param {Object} lead - Lead object with all details
 * @param {function} onEditScript - Callback to open script editor
 */
const CallScriptModal = ({ show, onClose, lead, onEditScript }) => {
    const [script, setScript] = useState(null);
    const [personalizedScript, setPersonalizedScript] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (show && lead) {
            loadScript();
        }
    }, [show, lead]);

    const loadScript = () => {
        // Get default script (ID 1)
        const defaultScript = getScript(1);

        if (defaultScript) {
            setScript(defaultScript);
            const personalized = personalizeScript(defaultScript.content, lead);
            setPersonalizedScript(personalized);
        }
    };

    // Replace placeholders in script with lead data
    const personalizeScript = (scriptContent, leadData) => {
        if (!scriptContent || !leadData) return '';

        let personalized = scriptContent;

        // Replace basic lead fields
        personalized = personalized.replace(/{name}/g, leadData.name || '[Name]');
        personalized = personalized.replace(/{your_name}/g, '[Your Name]');
        personalized = personalized.replace(/{company}/g, leadData.company || '[Company]');
        personalized = personalized.replace(/{email}/g, leadData.email || '[Email]');
        personalized = personalized.replace(/{phone}/g, leadData.phonenumber || '[Phone]');

        // Replace custom fields
        personalized = personalized.replace(
            /{material_type}/g,
            getCustomField(leadData, 'Material Type') || '[Material Type]'
        );
        personalized = personalized.replace(
            /{frequency}/g,
            getCustomField(leadData, 'Frequency') || '[Frequency]'
        );
        personalized = personalized.replace(
            /{number_of_boxes}/g,
            getCustomField(leadData, 'Number of Boxes') || '[Number of Boxes]'
        );
        personalized = personalized.replace(
            /{quoted}/g,
            getCustomField(leadData, 'Quoted') || '[Quote Amount]'
        );
        personalized = personalized.replace(
            /{boxes}/g,
            getCustomField(leadData, 'Boxes') || '[Boxes]'
        );
        personalized = personalized.replace(
            /{date_of_service}/g,
            getCustomField(leadData, 'Date of Service') || '[Service Date]'
        );

        return personalized;
    };

    const handleCopyScript = () => {
        navigator.clipboard.writeText(personalizedScript);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-green-600 to-blue-600 text-white">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-lg">
                            <Phone size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">Call Script</h2>
                            <p className="text-sm text-green-100">{lead?.name || 'Lead'}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onEditScript}
                            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                            title="Edit Script Template"
                        >
                            <Edit2 size={20} />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                            title="Close"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>

                {/* Lead Information Panel */}
                <div className="p-6 bg-blue-50 border-b border-blue-200">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Lead Information</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                            <span className="text-gray-600">Customer:</span>
                            <p className="font-semibold text-gray-800">{lead?.name || 'N/A'}</p>
                        </div>
                        <div>
                            <span className="text-gray-600">Phone:</span>
                            <p className="font-semibold text-gray-800">
                                {lead?.phonenumber || 'N/A'}
                            </p>
                        </div>
                        <div>
                            <span className="text-gray-600">Email:</span>
                            <p className="font-semibold text-gray-800 truncate">
                                {lead?.email || 'N/A'}
                            </p>
                        </div>
                        <div>
                            <span className="text-gray-600">Company:</span>
                            <p className="font-semibold text-gray-800">{lead?.company || 'N/A'}</p>
                        </div>
                        <div>
                            <span className="text-gray-600">Address:</span>
                            <p className="font-semibold text-gray-800 truncate">
                                {lead?.address || 'N/A'}
                            </p>
                        </div>
                        <div>
                            <span className="text-gray-600">Status:</span>
                            <p className="font-semibold text-gray-800">
                                {lead?.status_name || 'N/A'}
                            </p>
                        </div>
                    </div>

                    {/* Custom Fields */}
                    {lead?.customfields && lead.customfields.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-blue-300">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                                Service Details
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                {lead.customfields.map((field, index) => (
                                    field.value && (
                                        <div key={index}>
                                            <span className="text-gray-600">{field.label}:</span>
                                            <p className="font-semibold text-gray-800">
                                                {field.value}
                                            </p>
                                        </div>
                                    )
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Script Content */}
                <div className="p-6 overflow-y-auto max-h-96">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">
                            {script?.name || 'Call Script'}
                        </h3>
                        <button
                            onClick={handleCopyScript}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-semibold"
                        >
                            {copied ? (
                                <>
                                    <Check size={16} className="text-green-600" />
                                    Copied!
                                </>
                            ) : (
                                <>
                                    <Copy size={16} />
                                    Copy Script
                                </>
                            )}
                        </button>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                        <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                            {personalizedScript || 'No script available'}
                        </div>
                    </div>

                    {/* Tips */}
                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                            <Phone size={16} />
                            Call Tips
                        </h4>
                        <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                            <li>Speak clearly and professionally</li>
                            <li>Listen actively to customer responses</li>
                            <li>Take notes during the call</li>
                            <li>Schedule follow-ups as needed</li>
                            <li>Update lead status after the call</li>
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                    >
                        Close
                    </button>
                    {lead?.phonenumber && (
                        <a
                            href={`tel:${lead.phonenumber}`}
                            className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg flex items-center justify-center gap-2"
                        >
                            <Phone size={20} />
                            Call Now
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CallScriptModal;
